import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ProviderLocation, ProviderAppointment } from '../api/providers';

// Provider interface - address fields removed, now in locations array
export interface Provider {
	id: number;
	name: string;
	totalAppointments: number;
	totalRevenue: number;
	completedAppointments: number;
	email: string;
	businessName: string;
	phone: string;
	joinedDate: string;
	baseBbpFee: number;
	bbpFee: number;
	bookingEnabled: boolean;
	notes?: string;
	industry?: string;
	accountStatus: boolean;
	averageRating: number;
	totalRatings: number;
	createdAt: string;
	updatedAt: string;
	locations: ProviderLocation[];
	recentAppointments: ProviderAppointment[];
}

// Pagination info from API
export interface PaginationInfo {
	page: number;
	size: number;
	totalElements: number;
	totalPages: number;
	first: boolean;
	last: boolean;
}

// Loading states
export type LoadingState = 'idle' | 'loading' | 'loading-more' | 'error';

interface ProvidersState {
	// Data
	providers: Provider[];
	pagination: PaginationInfo | null;

	// Loading & Error states
	loadingState: LoadingState;
	error: string | null;

	// Cache management
	shouldInvalidate: boolean;
	lastFetchedAt: number | null;

	// Actions
	setProviders: (providers: Provider[], pagination?: PaginationInfo) => void;
	appendProviders: (providers: Provider[], pagination: PaginationInfo) => void;
	addProvider: (provider: Provider) => void;
	updateProvider: (provider: Provider) => void;
	getProviderById: (id: number) => Provider | undefined;

	// Loading state actions
	setLoadingState: (state: LoadingState) => void;
	setError: (error: string | null) => void;

	// Cache actions
	invalidateList: () => void;
	clearInvalidation: () => void;
	resetList: () => void;
}

export const useProvidersStore = create<ProvidersState>()(
	devtools(
		(set, get) => ({
			// Initial state
			providers: [], // Start empty, will be populated from API
			pagination: null,
			loadingState: 'idle',
			error: null,
			shouldInvalidate: true, // Start as true to trigger initial API fetch
			lastFetchedAt: null,

			// Set providers (replace all - for initial load or refresh)
			setProviders: (providers, pagination) =>
				set({
					providers,
					pagination: pagination || null,
					loadingState: 'idle',
					error: null,
					lastFetchedAt: Date.now(),
				}),

			// Append providers (for infinite scroll)
			appendProviders: (newProviders, pagination) =>
				set(state => ({
					providers: [...state.providers, ...newProviders],
					pagination,
					loadingState: 'idle',
				})),

			// Add a single provider (after creation)
			addProvider: provider =>
				set(state => ({
					providers: [provider, ...state.providers],
					// Invalidate to ensure fresh data on next visit
					shouldInvalidate: true,
				})),

			// Update a provider
			updateProvider: provider =>
				set(state => ({
					providers: state.providers.map(p => (p.id === provider.id ? provider : p)),
				})),

			// Get provider by ID (for detail page cache)
			getProviderById: id => {
				return get().providers.find(p => p.id === id);
			},

			// Loading state management
			setLoadingState: loadingState => set({ loadingState }),
			setError: error => set({ error, loadingState: 'error' }),

			// Cache invalidation
			invalidateList: () => set({ shouldInvalidate: true }),
			clearInvalidation: () => set({ shouldInvalidate: false }),

			// Reset list (clear all data)
			resetList: () =>
				set({
					providers: [],
					pagination: null,
					loadingState: 'idle',
					error: null,
					lastFetchedAt: null,
				}),
		}),
		{ name: 'providers-store' }
	)
);

export default useProvidersStore;
